import type { NlsKey } from '../../nls/strings_nls'
import type { WorkspaceRole } from '../../types/workspace'
import type { OpsPhoneItemKey } from '../../../functions-common/src/ops/phoneItems'
import { isAnyOpsSectorEnabled, isModuleEnabled, type WorkspaceModuleSource } from '../../utils/workspaceModules'
import { tabLayoutForUseCase } from '../../utils/onboardingPresets'
import {
  getCatalogForSurface,
  HELPER_TAB_KEYS,
  MEMBER_TAB_KEYS,
  type Surface,
  type TabDef,
  type TabKey,
} from './tabCatalog'

export type UseCase = 'team' | 'shop' | 'school' | string

export interface LockedTabItem {
  key: TabKey
  reasonKey: NlsKey
}

export type LockMode = 'hidden' | 'visible-off'

export interface LockSpec {
  key: TabKey
  mode: LockMode
}

export interface TabBarPolicy {
  hidden?: TabKey[]
  lockedVisible?: LockedTabItem[]
  canManageInvites?: boolean
  canManageFieldPasses?: boolean
  eduJobs?: string[]
  opsPhoneItems?: OpsPhoneItemKey[]
  [key: string]: unknown
}

export interface WorkspaceTabDefaults {
  member?: {
    ADMIN?: TabKey[]
    EDITOR?: TabKey[]
    VIEWER?: TabKey[]
  }
  helper?: TabKey[]
  locks?: {
    member?: {
      ADMIN?: LockSpec[]
      EDITOR?: LockSpec[]
      VIEWER?: LockSpec[]
    }
    helper?: LockSpec[]
  }
}

export interface ResolveTabBarInput {
  surface: Surface
  role: WorkspaceRole
  workspace?: WorkspaceModuleSource
  policy?: TabBarPolicy
  workspaceDefault?: TabKey[]
  workspaceDefaults?: WorkspaceTabDefaults
  personal?: TabKey[]
  useCase?: UseCase
  /**
   * Helper policy tabs slot (R9 S15).
   * Merged into policy.hidden. Unused until R9 S15 lands.
   */
  helperPolicyTabs?: TabKey[]
  /**
   * Person hidden pages slot (R21 TM10).
   * Merged into policy.hidden. Unused until R21 TM10 lands.
   */
  personHiddenPages?: TabKey[]
}

export interface ResolveTabBarResult {
  pinned: TabKey[] // length <= 4, [0] is fixed first
  more: TabKey[]
  locked: LockedTabItem[]
}

/**
 * Infers onboarding use-case preset from enabled modules when not stored (TB-D16).
 * Priority: Stock on -> shop; Teaching/Training on -> school; otherwise -> team.
 */
export function inferUseCaseFromModules(
  workspace?: WorkspaceModuleSource,
): 'shop' | 'school' | 'team' {
  if (!workspace) return 'team'
  if (isModuleEnabled(workspace, 'stock')) return 'shop'
  if (isModuleEnabled(workspace, 'teaching') || isModuleEnabled(workspace, 'training')) return 'school'
  return 'team'
}

/**
 * Maps WorkspaceRole explicitly to its default group in WorkspaceTabDefaults (no fallthrough).
 */
export function getWorkspaceDefaultForRole(
  defaults: WorkspaceTabDefaults | undefined,
  surface: Surface,
  role: WorkspaceRole,
): TabKey[] | undefined {
  if (!defaults) return undefined

  if (surface === 'helper') {
    switch (role) {
      case 'OWNER':
        return defaults.helper
      case 'ADMIN':
        return defaults.helper
      case 'EDITOR':
        return defaults.helper
      case 'VIEWER':
        return defaults.helper
      case 'READONLY':
        return defaults.helper
      case 'HELPER':
        return defaults.helper
      case 'LEARNER':
        return defaults.helper
    }
  }

  switch (role) {
    case 'OWNER':
      return defaults.member?.ADMIN
    case 'ADMIN':
      return defaults.member?.ADMIN
    case 'EDITOR':
      return defaults.member?.EDITOR
    case 'VIEWER':
      return defaults.member?.VIEWER
    case 'READONLY':
      return defaults.member?.VIEWER
    case 'HELPER':
      return defaults.helper
    case 'LEARNER':
      return defaults.member?.VIEWER
  }
}

/**
 * Maps WorkspaceRole explicitly to its lock group in WorkspaceTabDefaults (no fallthrough).
 * Owners are never locked out of team.
 */
export function getWorkspaceLocksForRole(
  defaults: WorkspaceTabDefaults | undefined,
  surface: Surface,
  role: WorkspaceRole,
): LockSpec[] | undefined {
  if (!defaults?.locks) return undefined

  let specs: LockSpec[] | undefined

  if (surface === 'helper') {
    switch (role) {
      case 'OWNER':
        specs = defaults.locks.helper
        break
      case 'ADMIN':
        specs = defaults.locks.helper
        break
      case 'EDITOR':
        specs = defaults.locks.helper
        break
      case 'VIEWER':
        specs = defaults.locks.helper
        break
      case 'READONLY':
        specs = defaults.locks.helper
        break
      case 'HELPER':
        specs = defaults.locks.helper
        break
      case 'LEARNER':
        specs = defaults.locks.helper
        break
    }
  } else {
    switch (role) {
      case 'OWNER':
        specs = defaults.locks.member?.ADMIN
        break
      case 'ADMIN':
        specs = defaults.locks.member?.ADMIN
        break
      case 'EDITOR':
        specs = defaults.locks.member?.EDITOR
        break
      case 'VIEWER':
        specs = defaults.locks.member?.VIEWER
        break
      case 'READONLY':
        specs = defaults.locks.member?.VIEWER
        break
      case 'HELPER':
        specs = defaults.locks.helper
        break
      case 'LEARNER':
        specs = defaults.locks.member?.VIEWER
        break
    }
  }

  if (role === 'OWNER' && specs) {
    return specs.filter((lock) => lock.key !== 'team')
  }
  return specs
}

/**
 * Returns built-in defaults by use case or kitSetup (TB-P2, TB-D16, OC23, SK-D14).
 * Reads workspace.kitSetup?.tabLayout first, ahead of tabLayoutForUseCase(workspace.useCase).
 */
export function getBuiltInDefaultTabs(
  surface: Surface,
  useCaseOrWorkspace?: string | { useCase?: string; kitSetup?: { tabLayout?: 'team' | 'shop' | 'school' } } | null,
  workspaceContext?: { kitSetup?: { tabLayout?: 'team' | 'shop' | 'school' } } | null,
): TabKey[] {
  let layout: 'team' | 'shop' | 'school' | null = null
  let useCaseStr: string | null = null

  if (typeof useCaseOrWorkspace === 'object' && useCaseOrWorkspace !== null) {
    layout = useCaseOrWorkspace.kitSetup?.tabLayout ?? null
    useCaseStr = useCaseOrWorkspace.useCase ?? null
  } else if (typeof useCaseOrWorkspace === 'string') {
    useCaseStr = useCaseOrWorkspace
    layout = workspaceContext?.kitSetup?.tabLayout ?? null
  }

  if (!layout && useCaseStr) {
    const normalized = useCaseStr.toLowerCase()
    if (normalized === 'team' || normalized === 'shop' || normalized === 'school') {
      layout = normalized as 'team' | 'shop' | 'school'
    } else {
      layout = tabLayoutForUseCase(useCaseStr)
    }
  }

  if (!layout) {
    layout = 'team'
  }

  const isShop = layout === 'shop'
  const isSchool = layout === 'school'

  if (surface === 'member') {
    if (isShop) return ['for-you', 'stock', 'board', 'nexus']
    if (isSchool) return ['for-you', 'class', 'nexus', 'board']
    return ['for-you', 'board', 'nexus', 'ai']
  }

  // helper surface
  if (isSchool) return ['tasks', 'school', 'field', 'chat']
  return ['tasks', 'field', 'chat', 'calendar']
}

/**
 * Checks whether the module for a tab is active in the workspace (D6).
 * class / school count as on when teaching OR training is on.
 */
export function isTabModuleActive(
  tabDef: TabDef,
  workspace?: WorkspaceModuleSource,
): boolean {
  if (tabDef.key === 'class' || tabDef.key === 'school') {
    return isModuleEnabled(workspace, 'teaching') || isModuleEnabled(workspace, 'training')
  }
  if (tabDef.key === 'ops') {
    return isAnyOpsSectorEnabled(workspace)
  }
  if (!tabDef.module) return true
  return isModuleEnabled(workspace, tabDef.module)
}

/**
 * Checks role and access predicate for a tab.
 */
function isRoleAllowed(
  tabDef: TabDef,
  role: WorkspaceRole,
  policy?: TabBarPolicy,
  workspace?: WorkspaceModuleSource,
): boolean {
  if (tabDef.accessPredicate) {
    const allowed = tabDef.accessPredicate({
      role,
      canManageInvites: policy?.canManageInvites,
      canManageFieldPasses: policy?.canManageFieldPasses,
      workspace,
      modules: workspace?.modules,
      eduJobs: policy?.eduJobs,
      opsPhoneItems: policy?.opsPhoneItems as OpsPhoneItemKey[] | undefined,
    })
    if (!allowed) return false
  }

  if (tabDef.roles && tabDef.roles.length > 0) {
    if (role === 'OWNER') return true
    if (!tabDef.roles.includes(role)) return false
  }

  return true
}

/**
 * Pure tabBar resolver (TB-P3, TB-D16).
 *
 * Layered resolution:
 * 1. Filter catalog for surface.
 * 2. Drop tabs with available === false.
 * 3. Drop tabs whose module is turned off.
 * 4. Drop tabs forbidden by role or policy.hidden.
 * 5. Handle lockedVisible (appear in locked list and more, but forbidden from pinned).
 * 6. Select candidate list: personal -> workspaceDefault -> built-in default for useCase.
 * 7. Force fixed first tab at index 0.
 * 8. Drop unknown or forbidden keys, drop duplicates, cap pinned at 4.
 * 9. Place remaining allowed tabs in more, ordered by catalog.
 */
export function resolveTabBar(input: ResolveTabBarInput): ResolveTabBarResult {
  const {
    surface,
    role,
    workspace,
    policy,
    personal,
    workspaceDefault,
    workspaceDefaults,
    helperPolicyTabs,
    personHiddenPages,
  } = input

  const catalog = getCatalogForSurface(surface)
  const fixedFirstKey: TabKey = surface === 'member' ? 'for-you' : 'tasks'
  const validKeys = new Set<TabKey>(
    surface === 'member' ? MEMBER_TAB_KEYS : HELPER_TAB_KEYS,
  )

  const hiddenPolicyKeys = new Set<TabKey>(policy?.hidden ?? [])
  // Merge helperPolicyTabs (R9 S15) and personHiddenPages (R21 TM10) slots into hidden policy
  if (helperPolicyTabs) {
    for (const key of helperPolicyTabs) hiddenPolicyKeys.add(key)
  }
  if (personHiddenPages) {
    for (const key of personHiddenPages) hiddenPolicyKeys.add(key)
  }

  const lockedPolicyMap = new Map<TabKey, NlsKey>()
  if (policy?.lockedVisible) {
    for (const item of policy.lockedVisible) {
      lockedPolicyMap.set(item.key, item.reasonKey)
    }
  }

  // Merge workspace role/helper locks into policy
  const roleLocks = getWorkspaceLocksForRole(workspaceDefaults, surface, role)
  if (roleLocks) {
    for (const lock of roleLocks) {
      // Owners are never locked out of team
      if (role === 'OWNER' && lock.key === 'team') continue

      if (lock.mode === 'hidden') {
        hiddenPolicyKeys.add(lock.key)
      } else if (lock.mode === 'visible-off') {
        const reasonKey: NlsKey =
          surface === 'helper'
            ? 'NLSTabLockedTurnedOffByAdmin'
            : 'NLSTabLockedOffForRole'
        lockedPolicyMap.set(lock.key, reasonKey)
      }
    }
  }

  // Determine which tabs are fully allowed and which are locked
  const allowedTabs = new Set<TabKey>()
  const lockedItems: LockedTabItem[] = []

  for (const tab of catalog) {
    // Drop unavailable tabs (e.g. stock before FI18, school before R28)
    if (tab.available === false) continue

    // Drop tabs whose module is off
    if (!isTabModuleActive(tab, workspace)) continue

    // Drop tabs hidden by policy
    if (hiddenPolicyKeys.has(tab.key)) continue

    // Check locked visible policy
    if (lockedPolicyMap.has(tab.key)) {
      lockedItems.push({
        key: tab.key,
        reasonKey: lockedPolicyMap.get(tab.key)!,
      })
      continue
    }

    // Check role and access predicate
    if (!isRoleAllowed(tab, role, policy, workspace)) {
      continue
    }

    allowedTabs.add(tab.key)
  }

  // Resolve base candidate list
  let candidateList: TabKey[] | undefined

  if (personal && personal.length > 0) {
    candidateList = personal
  } else if (workspaceDefault && workspaceDefault.length > 0) {
    candidateList = workspaceDefault
  } else {
    const roleDefault = getWorkspaceDefaultForRole(workspaceDefaults, surface, role)
    if (roleDefault && roleDefault.length > 0) {
      candidateList = roleDefault
    } else {
      const effectiveUseCase =
        input.useCase && input.useCase.trim() !== ''
          ? input.useCase
          : inferUseCaseFromModules(workspace)
      candidateList = getBuiltInDefaultTabs(
        surface,
        effectiveUseCase,
        workspace as { kitSetup?: { tabLayout?: 'team' | 'shop' | 'school' } } | undefined,
      )
    }
  }

  // Construct pinned tabs
  const pinned: TabKey[] = [fixedFirstKey]
  const seen = new Set<TabKey>([fixedFirstKey])

  for (const key of candidateList) {
    if (pinned.length >= 4) break
    if (!validKeys.has(key)) continue
    if (seen.has(key)) continue
    if (!allowedTabs.has(key)) continue

    pinned.push(key)
    seen.add(key)
  }

  // Construct more tabs: remaining allowed tabs in catalog order, plus locked items
  const pinnedSet = new Set<TabKey>(pinned)
  const more: TabKey[] = []

  for (const tab of catalog) {
    if (pinnedSet.has(tab.key)) continue
    if (allowedTabs.has(tab.key)) {
      more.push(tab.key)
    } else if (lockedPolicyMap.has(tab.key) && tab.available !== false && isTabModuleActive(tab, workspace)) {
      // Locked tabs also appear in More so users can see they exist and why they are locked
      more.push(tab.key)
    }
  }

  return {
    pinned,
    more,
    locked: lockedItems,
  }
}
